import React, { Fragment, useEffect, useRef, useState } from "react";
import {
	NavLink,
	RouteComponentProps,
	useHistory,
	withRouter
} from "react-router-dom";
import { useSelector } from "react-redux";
import "./index.less";
import Menu from "antd/lib/menu";
import Layout from "antd/lib/layout";
import {
	getDefaultGrowPrivateRoute,
	ZomentumRoutes,
	ZomentumStorageKeys,
	ZomentumWindow
} from "@/Contracts";
import {
	capitalizeFirstLetter,
	shouldHideLayoutForPath
} from "@/Contracts/Utils";
import { AuthToken } from "@/Contracts/Login";
import { cache } from "@/Contracts";
import LoginActionCreators from "@/Actions/Login";
import { isRouteAllowed } from "@/Components/Utils";
import UsersActionCreators from "@/Actions/User";
import { useFeatureFlag } from "@/Hooks/useFeature";
import useDispatch from "@/Components/Utils/useDispatch";
import Badge from "antd/lib/badge";
import ZTooltip from "@atoms/ZTooltip";
import ZIcon from "@atoms/ZIcon";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";
import Divider from "antd/lib/divider";
import { ILeftMenuItem } from "./interface";
import getLeftMenuItems from "./LeftMenuItems";
import SettingsMenuItems from "./SettingsMenuItems";
import ListingsMenuItems from "./ListingsMenuItems";
import {
	AllRolePermissions,
	PricingPlanNames,
	UserData,
	UserRole
} from "@/Contracts/Users";
import { useAuth0 } from "@auth0/auth0-react";
import ZBetaTag from "@atoms/ZBetaTag";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import SettingsActionCreators from "@/Actions/Settings";
import { GlobalState } from "@/Reducers/interface";
import { ZPopover } from "@atoms";
import ZTypography from "@atoms/ZTypography";
import { EFontWeights } from "@atoms/ZTypography/interface";
import useBreakpoint from "@/Hooks/useBreakpoint";
import { useGuideHook } from "@/Onboarding/hooks/useOnboardingGuide";
import { Steps, Stages, CheckListModule } from "@/Onboarding/contracts";
import { useInitOnboarding } from "@/Onboarding/hooks/useInitOnboarding";
import {
	useOnboardingSelector,
	useOnboardingDispatch
} from "@/Onboarding/store";
import OnboardingActionCreators from "@/Onboarding/store/actions";
import { OnboardingState } from "@/Onboarding/store/reducers";
import {
	UpdateCheckListBarDataPayload,
	UpdateCheckListBarDataRequest
} from "@/Onboarding/contracts";

const { ZText } = ZTypography;

declare let window: ZomentumWindow;

const WrappedChatIcon: React.FC<{ status: boolean }> = props => {
	if (props.status) return <Badge dot>{props.children}</Badge>;
	return <>{props.children}</>;
};

const WrappedMenuItem: React.FC<{
	title: string;
	onMouseDown?: () => void;
}> = props => {
	return (
		<>
			<div className="menu-item-container h-full	relative">
				{props.children}
				<ZTooltip showtooltip={true} title={props.title}>
					<div
						className="mask h-full	absolute top-0"
						onMouseDown={props.onMouseDown}
					/>
				</ZTooltip>
			</div>
		</>
	);
};

export interface IAppLeftMenu extends RouteComponentProps {
	readonly updateServiceWorker: (
		registration: ServiceWorkerRegistration
	) => void;
}

const AppLeftMenu: React.FC<IAppLeftMenu> = props => {
	const dispatch = useDispatch();
	const auth0 = useAuth0();
	const {
		isLoggedIn,
		user,
		isBannerOpen,
		appUpdateBannerVisible,
		permissions,
		isAuthenticatedFromLogin,
		shouldShowFreshChatBubble
	} = useSelector((state: GlobalState) => ({
		isLoggedIn: state.login.isLoggedIn,
		user: state.user.userData,
		isBannerOpen:
			state.bannerNotification.isOpen ||
			state.bannerNotification.isPaymentDueBannerOpen,
		appUpdateBannerVisible: state.settings.appUpdateBannerVisible,
		permissions: state.user.userData.roles_permissions?.permissions,
		isAuthenticatedFromLogin: state.login.isAuthenticatedFromSignIn,
		shouldShowFreshChatBubble:
			state.uiConfig.showFreshChatUnreadMessageBubble
	}));

	const [settingsMenuCollapsed, setSettingMenuCollapsed] =
		useState<boolean>(true);
	const [listingsMenuCollapsed, setListingsMenuCollapsed] =
		useState<boolean>(true);

	const settingsMenuRef = useRef<HTMLDivElement>(null);
	const listingsMenuRef = useRef<HTMLDivElement>(null);
	const enableHubSpotChatFeature = useFeatureFlag("hubspot_chat");
	const onboardingFlag = useFeatureFlag("in_app_onboarding");
	const showPartnerAlignFeaturesFlag = useFeatureFlag(
		"show_partner_align_features"
	);

	const [serviceWorkerRegistration, setServiceWorkerRegistration] =
		useState<ServiceWorkerRegistration | null>(null);

	const [classForMoreMenuItem, setClassForMoreMenuItem] =
		useState<boolean>(false);
	const [showMoreMenuItem, setShowMoreMenuItem] = useState<boolean>(false);

	const {
		checkListBarData,
		isOnboardingModuleActive,
		onboardingModuleOrder
	} = useOnboardingSelector((state: OnboardingState) => {
		const isChecklistActive = state.checkListBarData?.is_checklist_active;
		const isOnboardingModuleActive =
			isChecklistActive && state.currentGuideProgress.activeModule;
		return {
			checkListBarData: state.checkListBarData,
			isOnboardingModuleActive,
			onboardingModuleOrder: state.moduleOrder
		};
	});

	const onboardingDispatch = useOnboardingDispatch();
	const { initOnboarding } = useInitOnboarding();

	if (
		appUpdateBannerVisible &&
		process.env.REACT_APP_NODE_ENV !== "production"
	) {
		console.log("update is available");
		console.log({ serviceWorker: serviceWorkerRegistration });
	}

	const handleLeftMenuClick = (event: MouseEvent) => {
		if (
			settingsMenuRef.current &&
			!settingsMenuRef.current.contains(event.target as Node) &&
			!settingsMenuCollapsed
		) {
			setSettingMenuCollapsed(true);
		}
		if (
			listingsMenuRef.current &&
			!listingsMenuRef.current.contains(event.target as Node) &&
			!listingsMenuCollapsed
		) {
			setListingsMenuCollapsed(true);
		}
	};

	const handleUpdateServiceWorkerEvent = ($event: Event) => {
		dispatch(SettingsActionCreators.showAppUpdateBanner());
		const $customEvent = $event as CustomEvent<{
			registration: ServiceWorkerRegistration;
		}>;

		console.log(
			"current service worker --> ",
			$customEvent.detail.registration
		);

		if (
			$customEvent &&
			$customEvent.detail &&
			$customEvent.detail.registration
		) {
			setServiceWorkerRegistration($customEvent.detail.registration);
		}
	};
	const handleUpdateServiceWorker = () => {
		if (props.updateServiceWorker && serviceWorkerRegistration) {
			console.log(
				"triggering update with --> ",
				serviceWorkerRegistration
			);
			props.updateServiceWorker(serviceWorkerRegistration);
		}
	};

	useEffect(() => {
		document.addEventListener("mousedown", handleLeftMenuClick);
		document.addEventListener(
			"updateServiceWorker",
			handleUpdateServiceWorkerEvent
		);
		return () => {
			document.removeEventListener(
				"updateServiceWorker",
				handleUpdateServiceWorkerEvent
			);
			document.removeEventListener("mousedown", handleLeftMenuClick);
		};
	});

	const handleLogoutUser = () => {
		destroyThirdPartyScripts();
		dispatch(LoginActionCreators.logout(auth0));
		dispatch(UsersActionCreators.resetUserState());
		props.history.push(ZomentumRoutes.Login);
	};

	const destroyThirdPartyScripts = () => {
		destroyHubSpotConversations();
		destroyFreshchat();
	};

	const destroyHubSpotConversations = () => {
		window.HubSpotConversations?.widget?.remove?.();
	};
	const destroyFreshchat = () => {
		if (process.env.REACT_APP_FRESHCHAT && window.fcWidget) {
			window.fcWidget.destroy();
		}
	};

	const [isHubspotChatShowing, setIsHubspotChatShowing] =
		useState<boolean>(false);

	const isHubspotChatShowingRef = useRef(false);

	const hubspotChatMutationObserver = useRef<MutationObserver | null>();

	useEffect(() => {
		isHubspotChatShowingRef.current = isHubspotChatShowing;
	}, [isHubspotChatShowing]);

	const handleHubSpotVisibility = (hide: boolean) => {
		if (window.HubSpotConversations) {
			const hubspotFrame = document.getElementById(
				"hubspot-messages-iframe-container"
			);
			if (hide) {
				hubspotFrame?.classList?.remove?.("show-hubspot-iframe");
				window.HubSpotConversations?.widget?.close?.();
			} else {
				hubspotFrame?.classList?.add?.("show-hubspot-iframe");
				window.HubSpotConversations?.widget?.open?.();
			}
			setIsHubspotChatShowing(!hide);
			const elem = hubspotFrame?.querySelector(
				".shadow-container"
			) as HTMLElement;
			if (!hide) {
				elem &&
					hubspotChatMutationObserver.current?.observe(elem, {
						attributes: true
					});
			} else {
				hubspotChatMutationObserver.current?.disconnect();
			}
		}
	};

	const showConversationsPopup = () => {
		handleHubSpotVisibility(isHubspotChatShowing);
		if (window.fcWidget) {
			window.fcWidget.open();
			window.fcWidget.show();
		}
	};

	const history = useHistory();

	useEffect(() => {
		hubspotChatMutationObserver.current = new MutationObserver(
			mutationList => {
				mutationList.forEach(mutation => {
					if (
						mutation.type === "attributes" &&
						mutation.attributeName === "class" &&
						isHubspotChatShowingRef.current
					) {
						const elem = document
							.getElementById("hubspot-messages-iframe-container")
							?.querySelector(".shadow-container") as HTMLElement;
						const hasActive = elem.classList.contains("active");
						!hasActive &&
							window.HubSpotConversations?.widget?.open?.();
					}
				});
			}
		);
		const handleOpenHubSpotConversation = () => {
			handleHubSpotVisibility(false);
		};
		document.addEventListener(
			"openHubSpotConversation",
			handleOpenHubSpotConversation
		);
		return () => {
			handleHubSpotVisibility(true);
			document.removeEventListener(
				"openHubSpotConversation",
				handleOpenHubSpotConversation
			);
		};
	}, []);

	useEffect(() => {
		if (isOnboardingModuleActive) {
			handleHubSpotVisibility(true);
		}
	}, [isOnboardingModuleActive, isHubspotChatShowing]);

	const moveToRoute = (
		menuItem: ILeftMenuItem,
		isMoreMenuClicked: boolean
	) => {
		const { route } = menuItem;
		if (route?.includes(ZomentumRoutes.Settings)) {
			setSettingMenuCollapsed(!settingsMenuCollapsed);
		} else if (route?.includes(ZomentumRoutes.VendorMarketplaceListings)) {
			setListingsMenuCollapsed(false);
		} else {
			if (appUpdateBannerVisible) {
				console.log(
					"updating service worker with --> ",
					serviceWorkerRegistration
				);
				handleUpdateServiceWorker();
			}
			route && history.push(route);
		}

		setClassForMoreMenuItem(isMoreMenuClicked);
		setShowMoreMenuItem(false);
	};
	const settingsMenuItems = [...SettingsMenuItems()];
	const listingsMenuItems = [...ListingsMenuItems()];

	settingsMenuItems.splice(0, 0, {
		title: user.name.full,
		permission: null,
		disabled: true
	});

	const isPermissionEnabled = (permission?: AllRolePermissions | null) => {
		if (
			permission === null ||
			permission === AllRolePermissions.COMPANY_CONNECT
		)
			return true;
		else return permission && permissions?.includes(permission);
	};
	const isApprovalRulesAllowed = useFeatureAccess(
		PricingPlanNames.EXPAND,
		UserRole.PrimaryRole
	);

	const movePreferencesToGlobal = useFeatureFlag("move_preferences_global");

	if (movePreferencesToGlobal) {
		const preferencesItemIndex = settingsMenuItems.findIndex(
			menuItem => menuItem.route === ZomentumRoutes.Preferences
		);
		if (preferencesItemIndex !== -1) {
			settingsMenuItems[preferencesItemIndex].title = "Preferences";
		}
	}

	if (isApprovalRulesAllowed) {
		settingsMenuItems.splice(9, 0, {
			route: ZomentumRoutes.DocumentApproval,
			icon: EIcons.APPROVALRULES,
			title: "Approval rules",
			permission: null
		});
	}

	const renderSettingsMenu = () => {
		return (
			<div ref={settingsMenuRef}>
				<Layout.Sider
					collapsible={true}
					collapsed={settingsMenuCollapsed}
					trigger={null}
					className={`settings-sider overflow-auto fixed ${
						isBannerOpen ? "banner-open" : ""
					}`}
					collapsedWidth={0}
					width={256}
				>
					{user && (
						<>
							<Menu mode="inline" openKeys={["Email"]}>
								{settingsMenuItems.map(
									(menuItem: ILeftMenuItem) =>
										menuItem.disabled ? (
											<Menu.Item
												key={menuItem.title}
												disabled
												className="fixed-menu-tile"
											>
												{menuItem.title}
											</Menu.Item>
										) : (
											menuItem.route &&
											isPermissionEnabled(
												menuItem.permission
											) &&
											isRouteAllowed(
												menuItem.route,
												(user as UserData).role
											) && (
												<Menu.Item
													key={menuItem.route}
													onClick={() => {
														setSettingMenuCollapsed(
															true
														);
													}}
													className={
														props.location.pathname.includes(
															menuItem.route
														)
															? "ant-menu-item-selected"
															: ""
													}
												>
													<NavLink
														to={menuItem.route}
														exact={true}
														strict={true}
													>
														{menuItem.componentIcon ? (
															<menuItem.componentIcon />
														) : (
															<ZIcon
																name={
																	menuItem.icon
																}
																size="sm"
															/>
														)}
														{menuItem.title}
													</NavLink>
												</Menu.Item>
											)
										)
								)}
							</Menu>
							<Menu mode="inline">
								<Menu.Item
									key="logout-title"
									className="logout-menu-title"
									onClick={handleLogoutUser}
								>
									<NavLink to="#">
										<ZIcon name={EIcons.LOGOUT} size="sm" />
										Logout
									</NavLink>
								</Menu.Item>
							</Menu>
						</>
					)}
				</Layout.Sider>
			</div>
		);
	};

	const renderListingsMenu = () => {
		return (
			<div ref={listingsMenuRef}>
				<Layout.Sider
					collapsible={true}
					collapsed={listingsMenuCollapsed}
					trigger={null}
					className={`settings-sider overflow-auto fixed ${
						isBannerOpen ? "banner-open" : ""
					}`}
					collapsedWidth={0}
					width={256}
				>
					{user && (
						<>
							<Menu mode="inline" openKeys={["Email"]}>
								{listingsMenuItems.map(
									(menuItem: ILeftMenuItem) =>
										menuItem.disabled ? (
											<Menu.Item
												key={menuItem.title}
												disabled
												className="fixed-menu-tile"
											>
												{menuItem.title}
											</Menu.Item>
										) : (
											menuItem.route && (
												<Menu.Item
													key={menuItem.route}
													onClick={() => {
														setListingsMenuCollapsed(
															true
														);
													}}
													className={
														props.location.pathname.includes(
															`${window.location.origin}${menuItem.route}`
														)
															? "ant-menu-item-selected"
															: ""
													}
												>
													<NavLink
														to={menuItem.route}
														exact={true}
														strict={true}
													>
														<ZIcon
															name={menuItem.icon}
															size="sm"
														/>
														{menuItem.title}
													</NavLink>
												</Menu.Item>
											)
										)
								)}
							</Menu>
						</>
					)}
				</Layout.Sider>
			</div>
		);
	};

	const authToken = cache.get(ZomentumStorageKeys.Token, true) as AuthToken;

	const shouldShowSidebar =
		!shouldHideLayoutForPath(props.location.pathname) &&
		(isLoggedIn || (authToken && authToken.token));

	const leftMenuItems = getLeftMenuItems(props.location.pathname);

	const upfrontLeftMenuItem = leftMenuItems.filter(
		menuItem => menuItem.title !== "Align" && menuItem.title !== "Settings"
	);

	const moreLeftMenuItem = leftMenuItems.filter(
		menuItem => menuItem.title === "Align" || menuItem.title === "Settings"
	);

	const isSmallerScreenSize = useBreakpoint("(max-height: 750px)");

	const screenDimension =
		isSmallerScreenSize && upfrontLeftMenuItem.length > 7;

	const {
		GuideUI: DocumentMenuItemGuideUI,
		stepDetail,
		handleNext: handleDocumentGuideNext
	} = useGuideHook({
		stageId: Stages.DOCUMENT_INTRODUCTION,
		stepId: Steps.HIGHLIGHT_DOCUMENT_MENUITEM
	});

	const {
		GuideUI: AlignMenuItemGuideUI,
		stepDetail: alignGuideStepDetail,
		handleNext: handleAlignGuideNext
	} = useGuideHook({
		stageId: Stages.ALIGN_INTRODUCTION,
		stepId: Steps.HIGHLIGHT_ALIGN_MENUITEM
	});

	const {
		GuideUI: OpportunityMenuItemGuideUI,
		stepDetail: opportunityGuideStepDetail,
		handleNext: handleOpportunitiGuideNext
	} = useGuideHook({
		stageId: Stages.OPPORTUNITY_INTRODUCTION,
		stepId: Steps.HIGHLIGHT_OPPORTUNITY_MENUITEM
	});

	// TODO: Add types once TS infers the guide hook.
	const routeToGuideMapper: Partial<Record<ZomentumRoutes, any>> = {
		[ZomentumRoutes.DocumentsList]: {
			Guide: DocumentMenuItemGuideUI,
			stepDetail: stepDetail,
			handleNext: handleDocumentGuideNext
		},
		[ZomentumRoutes.Marketplace]: {
			Guide: AlignMenuItemGuideUI,
			stepDetail: alignGuideStepDetail,
			handleNext: handleAlignGuideNext
		},
		[ZomentumRoutes.OpportunityList]: {
			Guide: OpportunityMenuItemGuideUI,
			stepDetail: opportunityGuideStepDetail,
			handleNext: handleOpportunitiGuideNext
		}
	};

	const showResumeTutorial =
		onboardingFlag &&
		!checkListBarData?.is_checklist_active &&
		(checkListBarData?.active_module === null ||
			checkListBarData?.active_module !== null ||
			checkListBarData?.is_checklist_complete);

	const noOfModulesIncompleted = checkListBarData?.modules?.filter(
		(checkListModule: any) => !checkListModule.is_complete
	).length;

	const [resumeTutorialLoading, setResumeTutorialLoading] = useState(false);

	const resumeTutorial = async (activeModuleIndex: number | null) => {
		try {
			setResumeTutorialLoading(true);
			const requestPayload: UpdateCheckListBarDataPayload = {
				is_checklist_active: true
			};
			let activeModule: CheckListModule | undefined;
			if (activeModuleIndex !== null) {
				requestPayload.active_module = activeModuleIndex;
				const allModules = checkListBarData?.modules;
				activeModule = allModules?.[activeModuleIndex];
				if (activeModule) {
					activeModule.is_complete = false;
					activeModule.stages.forEach(
						stage => (stage.is_complete = false)
					);
				}
				requestPayload.modules = allModules;
			}
			const request: UpdateCheckListBarDataRequest = {
				requestPayload,
				hasAPIUpdate: true
			};

			await onboardingDispatch(
				OnboardingActionCreators.updateCheckListBarData(request)
			);
			setShowOnboardingCompletedModules(false);
			initOnboarding(isAuthenticatedFromLogin, false);
		} catch (error) {
			console.error(error);
		} finally {
			setResumeTutorialLoading(false);
		}
	};

	const renderLeftNavBarItems = (
		menuItems: ILeftMenuItem[],
		showTextWithIcon: boolean
	) => {
		return menuItems.map((menuItem: ILeftMenuItem) => {
			const hasMenuItem =
				menuItem.route &&
				isPermissionEnabled(menuItem.permission) &&
				isRouteAllowed(menuItem.route, (user as UserData).role);
			if (hasMenuItem) {
				const guideDetails =
					menuItem.route && routeToGuideMapper[menuItem.route];
				const handleMenuClick = () => {
					if (!showTextWithIcon) {
						moveToRoute(menuItem, true);
						guideDetails?.handleNext?.();
					}
				};
				const wrappedMenuItem = (
					<WrappedMenuItem
						title={`${menuItem.title}`}
						onMouseDown={handleMenuClick}
					>
						{menuItem.componentIcon ? (
							<menuItem.componentIcon />
						) : (
							<>
								<ZIcon
									name={menuItem.icon}
									color={EColors.GRAY}
									colorVariant={2}
									fill={menuItem.fillIcon}
								/>
								{showTextWithIcon && (
									<ZText
										className="ml-2 grey-1"
										colorVariant={6}
										weight={EFontWeights.MEDIUM}
									>
										{menuItem.title}
									</ZText>
								)}
							</>
						)}
						{menuItem.isBeta && <ZBetaTag className="absolute" />}
					</WrappedMenuItem>
				);
				const Guide = guideDetails?.Guide;
				return (
					<Menu.Item
						key={menuItem.route}
						title={null}
						className={`${
							menuItem.selected ? "ant-menu-item-selected" : ""
						} ${menuItem.isBeta ? "show-beta-tag" : ""} ${
							menuItem.className
						} ${
							classForMoreMenuItem && showTextWithIcon
								? "remove-selected-item"
								: ""
						}`}
						onClick={() =>
							showTextWithIcon
								? moveToRoute(menuItem, false)
								: null
						}
						onMouseOut={() =>
							showTextWithIcon
								? setClassForMoreMenuItem(false)
								: null
						}
						id={
							guideDetails?.Guide
								? guideDetails?.stepDetail?.guideUIProps
										?.highlightId
								: ""
						}
					>
						{Guide ? (
							<Guide key={menuItem.route}>
								{wrappedMenuItem}
							</Guide>
						) : (
							wrappedMenuItem
						)}
					</Menu.Item>
				);
			}
			return false;
		});
	};

	const hasHubspotChat =
		window.HubSpotConversations && enableHubSpotChatFeature;
	const hasSupportChat = window.fcWidget || hasHubspotChat;

	const [showOnboardingCompletedModules, setShowOnboardingCompletedModules] =
		useState(false);

	const getHelpPopupContent = () => {
		let menuItems: JSX.Element[] = [];
		if (showOnboardingCompletedModules) {
			menuItems = [
				...onboardingModuleOrder.map((module, index) => {
					return (
						<div
							onClick={() => resumeTutorial(index)}
							className="mb-3 cursor-pointer"
							key={`${module}-onboarding-module`}
						>
							<ZText
								className="grey-1"
								colorVariant={1}
								level={1}
							>
								Explore {capitalizeFirstLetter(module)}
							</ZText>
						</div>
					);
				}),
				<div
					key="revisit-walkthrough"
					onClick={() => setShowOnboardingCompletedModules(false)}
					className="cursor-pointer flex items-center mt-1"
				>
					<ZIcon
						name={EIcons.CHEVRONLEFT}
						color={EColors.GRAY}
						size="sm"
						className="mr-0.5 ml-0"
						colorVariant={2}
					/>
					<ZText className="grey-1" colorVariant={1} level={1}>
						Revisit Walkthrough
					</ZText>
				</div>
			];
		} else {
			menuItems.push(
				<ZText
					onClick={() =>
						window.open("https://help.zomentum.com/support/home")
					}
					className="cursor-pointer"
					colorVariant={1}
					level={1}
					key={"help-center"}
				>
					Help Centre
				</ZText>
			);
			if (showResumeTutorial) {
				menuItems.push(
					checkListBarData?.is_checklist_complete ? (
						<div
							onClick={() =>
								setShowOnboardingCompletedModules(true)
							}
							className="pt-4 cursor-pointer"
						>
							<ZText
								className="grey-1"
								colorVariant={1}
								level={1}
							>
								Revisit Walkthrough
							</ZText>
							<ZIcon
								name={EIcons.CHEVRONRIGHT}
								color={EColors.GRAY}
								size="sm"
								className="ml-0.5"
								colorVariant={2}
							/>
						</div>
					) : (
						<div
							onClick={() => resumeTutorial(null)}
							className="pt-4 cursor-pointer flex items-center"
						>
							<Badge
								count={noOfModulesIncompleted as number}
								size="small"
								color="geekblue"
								offset={[16, 10]}
								style={{ padding: "0 6px" }}
							>
								<ZText
									className="grey-1"
									colorVariant={1}
									level={1}
								>
									Resume Walkthrough
								</ZText>
							</Badge>
						</div>
					)
				);
			}
		}
		return (
			<div
				className={`flex flex-col ${
					resumeTutorialLoading ? "pointer-events-none" : ""
				}`}
			>
				{menuItems}
			</div>
		);
	};

	const AppSider = (
		<Fragment>
			<Layout.Sider
				data-testid="left-menu"
				collapsible={true}
				collapsed={true}
				trigger={null}
				collapsedWidth={64}
				className={`app-sider ${isBannerOpen ? "banner-open" : ""}`}
			>
				<Menu
					mode="inline"
					style={{ width: "fit-content" }}
					defaultSelectedKeys={[
						getDefaultGrowPrivateRoute(showPartnerAlignFeaturesFlag)
					]}
					selectedKeys={[props.location.pathname]}
					key="Zomentum-Left-Menu"
				>
					{renderLeftNavBarItems(
						screenDimension ? upfrontLeftMenuItem : leftMenuItems,
						false
					)}

					{screenDimension ? (
						<ZPopover
							overlayInnerStyle={{
								background: "#000000",
								zIndex: 50
							}}
							overlayClassName="more-menuItem-content"
							placement="right"
							size="xs"
							hideArrow={false}
							content={
								<Menu
									mode="inline"
									style={{ width: "fit-content" }}
									defaultSelectedKeys={[
										getDefaultGrowPrivateRoute(
											showPartnerAlignFeaturesFlag
										)
									]}
									selectedKeys={[props.location.pathname]}
								>
									{renderLeftNavBarItems(
										moreLeftMenuItem,
										true
									)}
								</Menu>
							}
							visible={showMoreMenuItem}
							trigger="hover"
							onVisibleChange={() =>
								setShowMoreMenuItem(!showMoreMenuItem)
							}
						>
							<Menu.Item
								key={"more-menuItem-icon"}
								title={null}
								className="more-menuItem"
								onMouseDown={() =>
									setShowMoreMenuItem(!showMoreMenuItem)
								}
							>
								<div className="flex justify-center">
									<ZIcon
										name={EIcons.ELLIPSIS}
										color={EColors.GRAY}
										colorVariant={6}
									/>
								</div>
							</Menu.Item>
						</ZPopover>
					) : null}

					<ZPopover
						overlayInnerStyle={{
							background: "#000000",
							zIndex: 50
						}}
						overlayClassName={onboardingFlag ? "" : "hidden"}
						placement="rightBottom"
						size="xs"
						hideArrow={true}
						trigger="hover"
						content={onboardingFlag && getHelpPopupContent()}
					>
						<div
							className={`help-menuItem ${
								hasSupportChat
									? "with-chat-banner"
									: hasSupportChat && isBannerOpen
									? "with-email-banner"
									: hasSupportChat && !isBannerOpen
									? "with-chat"
									: !window.fcWidget
									? "with-banner"
									: "without-chat-banner"
							} mb-10 ${onboardingFlag ? "cursor-default" : ""}`}
							onClick={() => {
								!onboardingFlag &&
									window.open(
										"https://zomentum-org.freshworks.com/login/auth/1657187157248?client_id=451979510707337272&redirect_uri=https%3A%2F%2Fzomentum.freshdesk.com%2Ffreshid%2Fcustomer_authorize_callback%3Fhd%3Dhelp.zomentum.com"
									);
							}}
						>
							<div className="flex justify-center cursor-pointer">
								<Badge
									count={
										showResumeTutorial &&
										!checkListBarData?.is_checklist_complete
											? (noOfModulesIncompleted as number)
											: 0
									}
									size="small"
									color="geekblue"
								>
									<ZIcon
										name={EIcons.HELP}
										color={EColors.GRAY}
										colorVariant={2}
									/>
								</Badge>
							</div>
							{hasSupportChat && !hasHubspotChat && (
								<Divider className="mb-n2 mt-2" />
							)}
						</div>
					</ZPopover>
					{hasSupportChat && (
						<Menu.Item
							key={"chat-popup"}
							title={null}
							className={`chat-menuitem ${
								isBannerOpen ? "banner-open" : ""
							} mb-11`}
							onClick={showConversationsPopup}
						>
							<div className="flex justify-center">
								<WrappedChatIcon
									status={shouldShowFreshChatBubble}
								>
									<ZIcon
										name={
											!hasHubspotChat ||
											!isHubspotChatShowing
												? EIcons.CIRCLECHATDOT
												: EIcons.CANCEL
										}
										color={EColors.GEEKBLUE}
										colorVariant={2}
									/>
								</WrappedChatIcon>
							</div>
						</Menu.Item>
					)}
				</Menu>
			</Layout.Sider>
			{renderSettingsMenu()}
			{renderListingsMenu()}
		</Fragment>
	);

	return shouldShowSidebar ? AppSider : <Fragment />;
};

export default withRouter(AppLeftMenu);
